<template>
  <div class="post-preview-section">
    <ul class="post-preview-section__tabs-row">
      <!-- Tab with logo -->
      <li
        :class="{
          'tab-with-logo': true,
          'tab-with-logo--active': socialNetwork === postPreviewActiveTab,
          'post-preview-section__tab-with-logo': true,
        }"
        v-for="socialNetwork in ['Facebook', 'Instagram']"
        :key="socialNetwork"
        @click="postPreviewActiveTab = socialNetwork"
      >
        <IconEmbedded
          v-if="socialNetwork === 'Facebook'"
          name="facebook"
          :class="{
            'tab-with-logo__logo': true,
            'tab-with-logo__logo--translucent': props.post.markedUsed && isUsedPostObvious,
            'tab-with-logo__logo--no-right-margin': ['mobile'].includes(screenSize),
          }"
          color="rgba(59, 89, 152, 1)"
        />
        <IconEmbedded
          v-else-if="socialNetwork === 'Instagram'"
          name="instagram"
          :class="{
            'tab-with-logo__logo': true,
            'tab-with-logo__logo--translucent': props.post.markedUsed && isUsedPostObvious,
            'tab-with-logo__logo--no-right-margin': ['mobile'].includes(screenSize),
            'tab-with-logo__logo--instagram': true,
          }"
          color="rgba(255, 255, 255, 1)"
        />

        <div
          v-if="!['tablet', 'tablet-large'].includes(screenSize)"
          :class="{
            'tab-with-logo__text': true,
            'tab-with-logo__text--translucent': props.post.markedUsed && isUsedPostObvious,
          }"
        >
          {{ socialNetwork }}
        </div>
      </li>
      <!-- / Tab with logo -->
    </ul>

    <div class="post-preview-section__post-snippet-wrap">
      <FbPostPreview
        v-if="postPreviewActiveTab === 'Facebook'"
        :class="{
          'post-preview-section__post-snippet': true,
          'post-preview-section__post-snippet--translucent': props.post.markedUsed && isUsedPostObvious,
        }"
        :images-list="props.assetIds"
        :fancyboxKey="props.fancyboxKey"
      >
        <template #textarea>
          <textarea
            v-if="props.isWithTextarea"
            class="post-preview-section__post-snippet-textarea"
            :value="props.caption"
            @input="$event => emit('update:caption', ($event.target as HTMLInputElement).value)"
            ref="domRefTextareaFb"
            placeholder="Caption"
          />
          <ClampedText v-else style="display: block; white-space: pre-wrap; font: 14px/18px sans-serif" :visibleLines="5">
            {{ props.caption }}
          </ClampedText>
        </template>
      </FbPostPreview>

      <InstPostPreview
        v-else-if="postPreviewActiveTab === 'Instagram'"
        :class="{
          'post-preview-section__post-snippet': true,
          'post-preview-section__post-snippet--translucent': props.post.markedUsed && isUsedPostObvious,
        }"
        :images-list="props.assetIds"
        :fancyboxKey="props.fancyboxKey"
      >
        <template #textarea>
          <textarea
            v-if="props.isWithTextarea"
            class="post-preview-section__post-snippet-textarea"
            :value="props.caption"
            @input="$event => emit('update:caption', ($event.target as HTMLInputElement).value)"
            ref="domRefTextareaInst"
            placeholder="Caption"
          />
          <ClampedText v-else style="display: block; white-space: pre-wrap; font: 14px/18px sans-serif" :visibleLines="5">
            {{ props.caption }}
          </ClampedText>
        </template>
      </InstPostPreview>

      <!-- Used mark -->
      <div v-if="props.post.markedUsed" class="used-mark post-preview-section__used-mark">
        <div v-if="!props.post.postUrl" class="used-mark__header">
          <h6 class="global-h6 used-mark__title">Where was it used?</h6>

          <InfoIconWithTooltip class="used-mark__icon" icon="trending-up_2-5" :width="300" style="position: relative"
            >Let us know where this was posted.<br />We're laying the groundwork to track performance so we can help you see what's working!
          </InfoIconWithTooltip>
        </div>

        <template v-if="props.post.postUrl">
          <span class="used-mark__link">Used for: <span v-html="linkifyStr(props.post.postUrl, { ...globalRoot.linkifyOptions, truncate: 25 })"></span></span>
        </template>

        <div v-else class="used-mark__add-link-form">
          <input class="global-text-input global-text-input--tiny used-mark__text-input" type="text" placeholder="https://facebook.com/example" v-model="_postUrl" />
          <SrpButton class="used-mark__add-button" size="tiny" @click="upsertPostUsageUrl" :isDisabled="isSavingPostUrl">
            <template v-if="isSavingPostUrl" #icon><LoadingIcon :size="14" /></template>
            Add
          </SrpButton>
        </div>
      </div>
      <!-- / Used mark -->

      <!-- Share and dislike buttons -->
      <div v-if="props.isWithShareAndDislikeButtons" class="share-and-dislike-buttons post-preview-section__share-and-dislike-buttons">
        <SrpButton class="share-and-dislike-buttons__share-button" size="small" :fill="props.post.markedUsed ? 'outlined' : 'solid'" @click="emit('clickCustomizeAndShare')">
          {{ props.readonlyMode ? "View readonly" : props.post.markedUsed ? "Use Again" : "Customize & Use" }}
        </SrpButton>

        <SrpButton :isDisabled="props.readonlyMode" class="share-and-dislike-buttons__dislike-button" size="small" color="gray" fill="outlined" @click="emit('clickDislike')">
          <template #icon><IconEmbedded name="dislike_2" :size="21" /></template>
        </SrpButton>
      </div>
      <!-- / Share and dislike buttons -->

      <!-- What post is based on -->
      <div v-if="(props.isWhatPostIsBasedOnVisible && props.post.contextType) || props.showDebug" class="post-preview-section__what-post-is-based-on">
        Post is based on {{ getCreatedFromPrefix(props.post.contextType) }}
        <RouterLink :to="getGeneratedFromUrl(props.post)" target="_blank" class="global-a">
          <IconEmbedded style="margin-right: 3px; position: relative; top: 4px" :name="getCreatedFromIconName(props.post.contextType)" :size="18" color="rgba(12, 135, 138, 1)" />
          {{ getCreatedFromFriendlyName(props.post.contextType) }}</RouterLink
        >

        <button class="ui mini right floated button" v-if="props.showDebug && props.debugData" @click.prevent="debugModalRef?.openModal()">Debug</button>
      </div>
      <!-- / What post is based on -->
    </div>
    <!--<TwPostPreview class="post-preview-section__snippet" />-->

    <!-- Debug Modal -->
    <ImageSelectionDebugModal ref="debugModalRef" :debugData="props.debugData" />
  </div>
</template>
<script setup lang="ts">
import { inject, Ref, ref, onMounted, watch } from "vue";
import { RouteLocationRaw, useRouter, RouterLink } from "vue-router";
import linkifyStr from "linkify-string";
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import FbPostPreview from "./FbPostPreview.vue";
import InstPostPreview from "./InstPostPreview.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import ClampedText from "@components/ui/ClampedText.vue";
import ImageSelectionDebugModal from "@components/ImageSelectAIDebug/ImageSelectionDebugModal.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";

// Types
import { ScreenSize } from "@contracts/screenSize";
import { ImageSelectAIDebugData, SocialMediaPostData, SocialPostTypeId } from "@contracts/shareStop";
import { IconName } from "@contracts/IconName";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

// Global variables & injections
const screenSize = inject("screenSize") as Ref<ScreenSize>;
const globalRoot = inject("globalRoot") as any;

// Router
const router = useRouter();

const props = withDefaults(
  defineProps<{
    assetIds: Array<string>;
    caption: string;
    isWithTextarea?: boolean;
    isWithShareAndDislikeButtons?: boolean;
    fancyboxKey: string;
    showDebug?: boolean;
    readonlyMode?: boolean;
    debugData?: ImageSelectAIDebugData | null;
    post?: SocialMediaPostData | null;
    isWhatPostIsBasedOnVisible?: boolean; // ex "Post is based on the Creator Visit"
  }>(),
  {
    assetIds: () => [],
    caption: "",
    isWithTextarea: false,
    isWithShareAndDislikeButtons: true,
    fancyboxKey: "",
    showDebug: false,
    debugData: null,
    readonlyMode: false,
    post: null,
    isWhatPostIsBasedOnVisible: true,
  }
);

const emit = defineEmits<{
  (e: "clickCustomizeAndShare"): void;
  (e: "update:caption", value: string): void;
  (e: "clickDislike"): void;
  (e: "updatePostUrl", value: string, etag: string): void;
}>();

// DOM refs ===================================================================
const domRefTextareaFb = ref<any>(null);
const domRefTextareaInst = ref<any>(null);
const debugModalRef = ref<InstanceType<typeof ImageSelectionDebugModal> | null>(null);

// Switch post preview tabs ===================================================
const postPreviewActiveTab = ref("Facebook");

// Define expose ==============================================================
defineExpose({
  domRefTextareaFb,
  domRefTextareaInst,
});

// I'm leaning toward making used posts not that different from non-used ones.
// Flipping this to true will invert that change
const isUsedPostObvious = true;

function getGeneratedFromUrl(post: SocialMediaPostData): RouteLocationRaw {
  // This is a quick workaround to handle sharesWith where posts are generated
  // from a customer that's not the post's customer.
  // The public creator visit endpoint has special logic for this.
  const customerIdForCollabSummaryPublic = "search";
  switch (post.contextType) {
    case "collabInput":
      return {
        name: "CollabSummaryPublic",
        params: {
          communityId: customerIdForCollabSummaryPublic,
          collabInputId: post.sourceEntityId,
        },
      };
    case "adventure":
      return {
        name: "ItineraryPreview",
        params: {
          itineraryId: post.contextId,
        },
      };
    case "location":
      return {
        name: "CollabSummaryPublic",
        params: {
          communityId: customerIdForCollabSummaryPublic,
          collabInputId: post.sourceEntityId,
        },
      };
    case "customer-upload":
      return {
        name: "CommunityPhotos",
        params: {
          pageId: post.customerId,
        },
        hash: `#group=my-uploads`,
      };
    case "community-upload":
      return {
        name: "CommunityPhotos",
        params: {
          pageId: post.customerId,
        },
        hash: `#group=community-upload`,
      };
    default:
      return "#";
  }
}

function getCreatedFromFriendlyName(contextType: SocialPostTypeId): string | null {
  switch (contextType) {
    case "collabInput":
      return "Creator Visit";
    case "adventure":
      return "Adventure";
    case "location":
      return "Visit Location";
    case "customer-upload":
      return "Your Uploads";
    case "community-upload":
      return "Community Uploads";
    default:
      return null;
  }
}

function getCreatedFromPrefix(contextType: SocialPostTypeId): string {
  switch (contextType) {
    case "collabInput":
      return "a";
    case "adventure":
      return "an";
    case "location":
      return "a";
    default:
      return "";
  }
}

function getCreatedFromIconName(contextType: SocialPostTypeId): IconName {
  switch (contextType) {
    case "collabInput":
      return "briefcase_2";
    case "adventure":
      return "compass_2";
    case "location":
      return "map-pin_2";
    case "customer-upload":
      return "upload_2";
    case "community-upload":
      return "users_2";
    default:
      return "upload_2";
  }
}

// Post url local copy ========================================================
const _postUrl = ref<string>("");

onMounted(() => {
  _postUrl.value = props.post.postUrl;
});

watch(
  () => props.post.postUrl,
  () => (_postUrl.value = props.post.postUrl)
);

// Upsert post usage url ======================================================
const isSavingPostUrl = ref<boolean>(false);

async function upsertPostUsageUrl() {
  isSavingPostUrl.value = true;

  const updatedPost = await ShareStopSummaryRepo.upsertPostUsageLink(props.post.customerId, props.post.id, _postUrl.value);

  isSavingPostUrl.value = false;

  emit("updatePostUrl", updatedPost.postUrl, updatedPost._etag);
}
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Tab with logo ==============================================================
.tab-with-logo {
  height: 42px;
  padding: 0 11px 2px 11px;
  box-sizing: border-box;
  border: 1px #d9d9d9 solid;
  border-width: 1px 1px 0 1px;
  border-radius: 6px 6px 0 0;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  font:
    700 15px/15px "Quicksand",
    sans-serif;
  background: #d9d9d9;
  cursor: pointer;
  user-select: none;

  &--active {
    background: #fff;
  }

  &__logo {
    width: 26px;
    height: 26px;
    margin-right: 3px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 27px;
    line-height: 27px;
    text-align: center;

    &--instagram {
      width: 20px;
      height: 20px;
      margin-right: 6px;
      border-radius: 5px;
      color: #fff;
      font-size: 18px;
      line-height: 18px;
      background: radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285aeb 90%);
    }

    &--tiktok {
      width: 20px;
      height: 20px;
      padding: 1px 0 0 1px;
      box-sizing: border-box;
      margin-right: 6px;
      border-radius: 5px;
      color: #fff;
      font-size: 12px;
      line-height: 12px;
      background: black;

      &::before {
        filter: drop-shadow(1px 0px 0px #fd3e3e) drop-shadow(-1px -1px 0px #4de8f4);
      }
    }

    &--no-right-margin {
      margin-right: 0;
    }

    &--translucent {
      opacity: 0.5;
    }
  }

  &__text {
    &--translucent {
      opacity: 0.5;
    }
  }
}

// Share and dislike buttons ==================================================
.share-and-dislike-buttons {
  gap: 12px;
  display: flex;
  justify-content: space-between;

  &__share-button {
  }

  &__dislike-button {
    width: 50px;
  }
}

// Used mark ==================================================================
.used-mark {
  align-items: center;

  &__header {
    display: flex;
    align-items: center;
    margin-top: 12px;
  }

  &__title {
    margin-right: 4px;
    font-weight: 500;
  }

  &__icon {
  }

  &__link {
    margin-right: 5px;
    position: relative;
    z-index: 2;
    font-weight: 500;
  }

  &__add-link-form {
    gap: 8px;
    margin-right: 5px;
    display: flex;
    position: relative;
    z-index: 2;
  }

  &__text-input {
  }

  &__add-button {
  }
}

// Post preview section =======================================================
.post-preview-section {
  display: flex;
  flex-direction: column;

  &__tabs-row {
    padding: 0;
    margin: 0 0 -1px;
    display: flex;
    list-style: none;
  }

  &__tab-with-logo {
    margin-right: 5px;
  }

  &__post-snippet-wrap {
    flex-grow: 1;
    padding: 12px 3px 13px;
    border: 1px #d7d7d7 solid;
    border-radius: 0 6px 6px 6px;
    display: flex;
    flex-direction: column;
    background: white;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
  }

  &__post-snippet {
    flex-grow: 1;

    &--translucent {
      opacity: 0.5;
    }
  }

  &__post-snippet-textarea {
    scroll-behavior: smooth;
  }

  &__used-mark {
    margin: 10px 12px 0;
  }

  &__divider {
    width: calc(100% - 24px);
    margin: 0 12px 18px;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  }

  &__share-and-dislike-buttons {
    margin: 15px 13px 17px;
  }

  &__what-post-is-based-on {
    @include standardFont;
    padding: 11px 0 0;
    margin: 0 12px;
    border-top: 1px rgba(0, 0, 0, 0.1) solid;
  }
}
</style>
